import { Loader2 } from 'lucide-react';
import React, {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    DEFAULT_SHIFT_ACTION_POLICY,
    type ShiftActionPolicy,
} from '@apps/web/components/phoebe_chat/assistant_response_types';
import {
    ChatInput,
    type ChatInputHandle,
    type ChatInputImageAttachment,
    type ChatInputSubmitMetadata,
} from '@apps/web/components/phoebe_chat/chat_input';
import { type PhoebeFooterStatus } from '@apps/web/components/phoebe_chat/event_transcript';
import { type MonitoringActivityControls } from '@apps/web/components/phoebe_chat/monitoring_configuration_card';
import { useTranscriptScroll } from '@apps/web/components/phoebe_chat_view/hooks/use_transcript_scroll';
import { PhoebeFooterStatusRow } from '@apps/web/components/phoebe_chat_view/messages/status_rows';
import { derivePhoebeFooterStatus } from '@apps/web/components/phoebe_chat_view/run_status/live_status';
import {
    EMPTY_DECIDING_CALL_IDS,
    getLatestMonitoringRun,
    MonitoringConfigurationCardForRun,
    type PhoebeAgentEventFeedChatViewState,
    PhoebeAgentTimelineEntries,
} from '@apps/web/components/phoebe_chat_view/timeline/transcript_rendering';
import { type ToolCardPresentation } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import {
    type AssistantMessageActionRenderer,
    type MessageArtifactRenderer,
    type ToolApprovalDecision,
    type ToolApprovalDecisionTarget,
    type ToolApprovalScope,
    type ToolArtifactRenderer,
    type ToolEntryInspectorHandler,
    type ToolOutputRenderer,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { cn } from '@apps/web/lib/utils';

const FEEDBACK_HIGHLIGHT_DURATION_MS = 2500;

export function PhoebeAgentEventFeedChatSurface({
    state,
    canOpenSubagentRun,
    debugPanelOpen = false,
    disabled = false,
    isSubmitting = false,
    onSubmit,
    readOnly = false,
    decidingCallIds = EMPTY_DECIDING_CALL_IDS,
    onToolDecision,
    leadingControl = null,
    placeholder = 'Ask or search anything...',
    organizationId = null,
    emptyState = 'No messages',
    isStopping = false,
    canStopRun = false,
    onStopRun,
    showGeneralOutreachProgressCards = false,
    showRecommendationTraceProgress = false,
    isMonitoringOutreach = false,
    monitoringControls = null,
    monitoringScheduleMetadata = null,
    renderToolArtifacts,
    renderMessageArtifacts,
    renderToolOutput,
    onInspectToolEntry,
    selectedToolEntryId = null,
    toolCardPresentation = 'default',
    organizationName = null,
    renderAssistantMessageActions,
    renderMessageFeedback,
    focusAgentItemId = null,
    emptyComposerPlacement = 'center',
    imageAttachments = [],
    onImageFilesSelected,
    onRemoveImageAttachment,
    imageUploadDisabled = false,
    submitOnShiftEnter = false,
    shiftActionPolicy = DEFAULT_SHIFT_ACTION_POLICY,
    groupActivityEntries = false,
    onOpenSubagentRun,
}: {
    readonly state: PhoebeAgentEventFeedChatViewState;
    readonly canOpenSubagentRun?: (childRunId: string) => boolean;
    readonly debugPanelOpen?: boolean;
    readonly disabled?: boolean;
    readonly isSubmitting?: boolean;
    readonly onSubmit: (
        content: string,
        metadata?: ChatInputSubmitMetadata,
    ) => Promise<boolean | void> | boolean | void;
    readonly readOnly?: boolean;
    readonly decidingCallIds?: Record<string, boolean>;
    readonly onToolDecision?: (
        target: ToolApprovalDecisionTarget,
        decision: ToolApprovalDecision,
        alwaysAllowScope?: ToolApprovalScope,
        argumentsOverride?: Record<string, unknown>,
    ) => Promise<void>;
    readonly leadingControl?: ReactNode;
    readonly placeholder?: string;
    readonly organizationId?: string | null;
    readonly emptyState?: ReactNode;
    readonly isStopping?: boolean;
    readonly canStopRun?: boolean;
    readonly onStopRun?: () => Promise<void> | void;
    readonly showGeneralOutreachProgressCards?: boolean;
    readonly showRecommendationTraceProgress?: boolean;
    readonly isMonitoringOutreach?: boolean;
    readonly monitoringControls?: MonitoringActivityControls | null;
    readonly monitoringScheduleMetadata?: Record<string, unknown> | null;
    readonly renderToolArtifacts?: ToolArtifactRenderer;
    readonly renderMessageArtifacts?: MessageArtifactRenderer;
    readonly renderToolOutput?: ToolOutputRenderer;
    readonly onInspectToolEntry?: ToolEntryInspectorHandler;
    readonly selectedToolEntryId?: string | null;
    readonly toolCardPresentation?: ToolCardPresentation;
    readonly organizationName?: string | null;
    readonly renderAssistantMessageActions?: AssistantMessageActionRenderer;
    readonly renderMessageFeedback?: AssistantMessageActionRenderer;
    readonly focusAgentItemId?: string | null;
    readonly emptyComposerPlacement?: 'center' | 'bottom';
    readonly imageAttachments?: ReadonlyArray<ChatInputImageAttachment>;
    readonly onImageFilesSelected?: (files: Array<File>) => void;
    readonly onRemoveImageAttachment?: (attachmentId: string) => void;
    readonly imageUploadDisabled?: boolean;
    readonly submitOnShiftEnter?: boolean;
    readonly shiftActionPolicy?: ShiftActionPolicy;
    readonly groupActivityEntries?: boolean;
    readonly onOpenSubagentRun?: (childRunId: string) => void;
}) {
    const chatInputRef = useRef<ChatInputHandle | null>(null);
    const noop = useCallback(() => undefined, []);
    const {
        transcriptContentRef,
        transcriptScrollAreaRef,
        stickTranscriptToBottomRef,
        handleTypewriterProgress,
    } = useTranscriptScroll({
        renderedTimelineEntryCount: state.timelineEntries.length,
        isAgentBusy: state.isAgentBusy,
    });
    const handledFocusAgentItemIdRef = useRef<string | null>(null);
    const feedbackHighlightTimeoutRef = useRef<number | null>(null);
    const [highlightedAgentItemId, setHighlightedAgentItemId] = useState<
        string | null
    >(null);

    useEffect(() => {
        if (focusAgentItemId === null) {
            handledFocusAgentItemIdRef.current = null;
            return;
        }
        if (handledFocusAgentItemIdRef.current === focusAgentItemId) {
            return;
        }
        const target = Array.from(
            transcriptContentRef.current?.querySelectorAll<HTMLElement>(
                '[data-agent-item-id]',
            ) ?? [],
        ).find(
            (element) => element.dataset['agentItemId'] === focusAgentItemId,
        );
        if (!target) {
            return;
        }

        handledFocusAgentItemIdRef.current = focusAgentItemId;
        stickTranscriptToBottomRef.current = false;
        const shouldReduceMotion =
            typeof window.matchMedia !== 'function' ||
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
            behavior: shouldReduceMotion ? 'auto' : 'smooth',
            block: 'center',
        });
        setHighlightedAgentItemId(focusAgentItemId);
        if (feedbackHighlightTimeoutRef.current !== null) {
            window.clearTimeout(feedbackHighlightTimeoutRef.current);
        }
        feedbackHighlightTimeoutRef.current = window.setTimeout(() => {
            setHighlightedAgentItemId(null);
            feedbackHighlightTimeoutRef.current = null;
        }, FEEDBACK_HIGHLIGHT_DURATION_MS);
    }, [
        focusAgentItemId,
        state.timelineEntries,
        stickTranscriptToBottomRef,
        transcriptContentRef,
    ]);

    useEffect(
        () => () => {
            if (feedbackHighlightTimeoutRef.current !== null) {
                window.clearTimeout(feedbackHighlightTimeoutRef.current);
            }
        },
        [],
    );

    const submitContent = useCallback(
        async (content: string, metadata?: ChatInputSubmitMetadata) => {
            const trimmedContent = content.trim();
            const hasReadyImageAttachment = imageAttachments.some(
                (attachment) => attachment.status === 'ready',
            );
            if (
                (!trimmedContent && !hasReadyImageAttachment) ||
                disabled ||
                isSubmitting
            ) {
                return;
            }

            const submitResult = await onSubmit(trimmedContent, metadata);
            if (submitResult === false) {
                return;
            }
            chatInputRef.current?.clear();
            stickTranscriptToBottomRef.current = true;
        },
        [
            disabled,
            imageAttachments,
            isSubmitting,
            onSubmit,
            stickTranscriptToBottomRef,
        ],
    );
    const monitoringFooterStatus = useMemo<PhoebeFooterStatus | null>(() => {
        if (!isMonitoringOutreach) {
            return null;
        }
        return derivePhoebeFooterStatus({
            runState: null,
            manualStatus: null,
            isMonitoring: true,
            isSending: false,
            isAwaitingRunCompletion: false,
            isStopping: false,
            isLiveStreamRunning: false,
            legacyAgentThinking: false,
            providerErrorBadge: null,
        });
    }, [isMonitoringOutreach]);
    const activeMonitoringRun = useMemo(
        () =>
            monitoringControls === null
                ? null
                : getLatestMonitoringRun(state.timelineEntries, null),
        [monitoringControls, state.timelineEntries],
    );
    const shouldShowMonitoringFooter =
        monitoringFooterStatus !== null &&
        !(
            monitoringControls !== null &&
            monitoringFooterStatus.canStopMonitoring
        );
    const usesWorkbenchPresentation = toolCardPresentation === 'minimal';
    const hasTimelineEntries = state.timelineEntries.length > 0;

    const transcriptContent = (
        <div
            ref={transcriptContentRef}
            className={cn(
                usesWorkbenchPresentation
                    ? 'relative mx-auto flex min-h-full w-full max-w-[62rem] min-w-0 flex-col gap-3 overflow-x-hidden px-5 pt-8 pb-10 sm:px-8 lg:pt-10'
                    : 'space-y-4 overflow-x-hidden p-4',
            )}
        >
            {hasTimelineEntries ? (
                <React.Fragment>
                    <PhoebeAgentTimelineEntries
                        entries={state.timelineEntries}
                        actionStateEntries={state.transcriptEntries}
                        recommendationOrganizationId={
                            organizationId ?? undefined
                        }
                        canOpenSubagentRun={canOpenSubagentRun}
                        debugPanelOpen={debugPanelOpen}
                        decidingCallIds={decidingCallIds}
                        onToolDecision={onToolDecision}
                        agentThinking={state.isAgentBusy}
                        timelineNowMs={state.timelineNowMs}
                        currentRunEventStreamIsGenerating={
                            state.currentRunEventStreamIsGenerating
                        }
                        latestAnimatedAssistantMessageId={
                            state.latestAnimatedAssistantMessageId
                        }
                        latestAnimatedThoughtId={state.latestAnimatedThoughtId}
                        onTypewriterProgress={handleTypewriterProgress}
                        onAssistantPromptAction={(request) => {
                            if (typeof request === 'string') {
                                void submitContent(request);
                            }
                        }}
                        showGeneralOutreachProgressCards={
                            showGeneralOutreachProgressCards
                        }
                        showRecommendationTraceProgress={
                            showRecommendationTraceProgress
                        }
                        shiftActionPolicy={shiftActionPolicy}
                        renderToolArtifacts={renderToolArtifacts}
                        renderMessageArtifacts={renderMessageArtifacts}
                        renderToolOutput={renderToolOutput}
                        onInspectToolEntry={onInspectToolEntry}
                        selectedToolEntryId={selectedToolEntryId}
                        toolCardPresentation={toolCardPresentation}
                        organizationName={organizationName}
                        renderAssistantMessageActions={
                            renderAssistantMessageActions
                        }
                        groupActivityEntries={groupActivityEntries}
                        renderMessageFeedback={renderMessageFeedback}
                        highlightedAgentItemId={highlightedAgentItemId}
                        onOpenSubagentRun={onOpenSubagentRun}
                    />
                    {monitoringFooterStatus ? (
                        <React.Fragment>
                            <MonitoringConfigurationCardForRun
                                run={activeMonitoringRun}
                                controls={monitoringControls}
                                monitoringScheduleMetadata={
                                    monitoringScheduleMetadata
                                }
                            />
                            {shouldShowMonitoringFooter ? (
                                <PhoebeFooterStatusRow
                                    status={monitoringFooterStatus}
                                />
                            ) : null}
                        </React.Fragment>
                    ) : null}
                </React.Fragment>
            ) : emptyState ? (
                <div
                    className={cn(
                        'text-muted-foreground flex h-full min-h-0 w-full items-center justify-center text-sm',
                        usesWorkbenchPresentation &&
                            'min-h-[22rem] items-start pt-16 text-[13px] text-[#829494]',
                    )}
                >
                    {emptyState}
                </div>
            ) : null}
        </div>
    );
    const composer = readOnly ? null : (
        <form
            onSubmit={(event) => {
                event.preventDefault();
                void submitContent(
                    chatInputRef.current?.getSerializedContent() ?? '',
                    { trigger: 'form_submit' },
                );
            }}
        >
            <ChatInput
                ref={chatInputRef}
                onSubmit={(content, metadata) => {
                    void submitContent(content, metadata);
                }}
                disabled={disabled || isSubmitting}
                isAgentBusy={state.isAgentBusy}
                canSubmitWhileAgentBusy={true}
                submitOnShiftEnter={submitOnShiftEnter}
                isStopping={isStopping}
                canStopRun={canStopRun}
                onStopRun={() => {
                    void (onStopRun ?? noop)();
                }}
                placeholder={placeholder}
                organizationId={organizationId}
                canRecordAudio={false}
                isRecording={false}
                isTranscribing={false}
                isVoiceInputDisabled={true}
                onStartRecording={noop}
                onStopRecording={noop}
                leadingControl={leadingControl}
                imageAttachments={imageAttachments}
                onImageFilesSelected={onImageFilesSelected}
                onRemoveImageAttachment={onRemoveImageAttachment}
                imageUploadDisabled={imageUploadDisabled}
                className={
                    usesWorkbenchPresentation
                        ? 'rounded-[10px] border-[#DBE3E3] bg-[#FFFFFF] shadow-[0_18px_50px_rgba(0,36,41,0.12)]'
                        : undefined
                }
            />
        </form>
    );

    if (usesWorkbenchPresentation) {
        const workbenchComposerBlock = composer ? (
            <div className="pointer-events-auto mx-auto w-full max-w-[58rem]">
                {state.isAgentBusy ? (
                    <div className="mb-2 flex justify-end px-1">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#DBE3E3] bg-[#FFFFFF] px-2.5 py-1 text-[11px] font-medium text-[#42595E] shadow-[0_1px_0_rgba(0,36,41,0.04)]">
                            <Loader2 className="size-3 animate-spin text-[#42595E]" />
                            <span>Working</span>
                        </div>
                    </div>
                ) : null}
                {composer}
            </div>
        ) : null;

        // The composer is a flex sibling of the scroll viewport so the
        // transcript is clipped at the composer's top edge instead of
        // rendering behind a floating overlay. The overlay form remains
        // only for the empty state, where the composer is centered over
        // an empty transcript.
        return (
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                <ScrollArea
                    ref={transcriptScrollAreaRef}
                    viewportClassName="[&>div]:!block"
                    // `flex flex-col` makes the Radix viewport a flex item
                    // (see scroll_area.tsx): the dock shell's max-height-
                    // clamped height is indefinite, so the viewport's
                    // percentage height resolves to auto and the transcript
                    // stops scrolling without this.
                    className="flex min-h-0 flex-1 flex-col"
                >
                    {transcriptContent}
                </ScrollArea>
                {workbenchComposerBlock ? (
                    hasTimelineEntries ? (
                        <div
                            data-testid="chat-composer-dock"
                            className="shrink-0 px-4 pt-2 pb-5 sm:px-8"
                        >
                            {workbenchComposerBlock}
                        </div>
                    ) : (
                        <div
                            className={cn(
                                'pointer-events-none absolute inset-x-0 z-10 px-4 sm:px-8',
                                emptyComposerPlacement === 'bottom'
                                    ? 'bottom-6'
                                    : 'top-1/2 -translate-y-1/2',
                            )}
                        >
                            {workbenchComposerBlock}
                        </div>
                    )
                ) : null}
            </div>
        );
    }

    return (
        <React.Fragment>
            <ScrollArea
                ref={transcriptScrollAreaRef}
                viewportClassName="[&>div]:!block"
                // Same flex-sizing opt-in as the workbench branch above so
                // this surface also scrolls under indefinite-height hosts.
                className="flex h-full min-h-0 flex-1 flex-col"
            >
                {transcriptContent}
            </ScrollArea>
            {composer ? <div className="px-4 pt-3 pb-3">{composer}</div> : null}
        </React.Fragment>
    );
}
